import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Card, Flex, Menu, Modal, Space, Spin, Typography, message } from 'antd';
import {
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProjectOutlined,
  StopOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useDispatch, useIntl, useSelector } from 'umi';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

import Drawer from '@/components/Drawer';
import {IAgentWithAvailability} from '@/typings/models/Assignee';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import styles from './AgentDrawer.less';
import AgentTasksTab from './AgentTasksTab';
import AgentProjectsTab from './AgentProjectsTab';
import AgentAppointmentsTab from './AgentAppointmentsTab';
import AgentAvailabilityTab from './AgentAvailabilityTab';
import AgentGeneralInfoTab from './AgentGeneralInfoTab';
import * as UsersSelectors from '@/selectors/usersSelectors';
import AvailabilityGlance from '@/components/Availability/AvailabilityGlance';
import { AvailabilityTimezoneTag } from '@/components/Availability/AvailabilityTimezone';
import { getAgentBranchTimezones } from '@/utils/user';
import {useHasAccess, useHasFeatureFlag} from '@/utils/hooks';
import FEATURE_FLAGS from '@/constants/featureFlags';
import PERMISSIONS from '@/constants/permissions';
import AgentBookingLinksTab from '@/components/Agent/AgentBookingLinksTab';
import AgentServicesTab from '@/components/Agent/AgentServicesTab';
import omit from 'lodash/omit';
import { deleteUserRequest, disableUserRequest, enableUserRequest } from '@/services/api/user';
import { isAgentActive } from '@/components/Agent/AgentName';
import { useIsMobile } from '@/hooks/useResponsive';

const { Text, Title } = Typography;
const AgentDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('tasks');
  const [drawerWidth, setDrawerWidth] = useState(800);
  // Phones: icon-only menu by default — the expanded 220px menu would crush the form.
  const [menuCollapsed, setMenuCollapsed] = useState(isMobile);
  const [dirty, setDirty] = useState(false);
  const agentDetailsDrawer = useSelector(UsersSelectors.selectAgentDetailsDrawer);
  // Central guard: every entry point (tags, avatars, /users/:id deep links)
  // funnels through this drawer, and its GET /users/:id needs users_view.
  const canView = useHasAccess([PERMISSIONS.Users.VIEW, PERMISSIONS.BUSINESS_PARTNER]);
  const agentId = canView ? agentDetailsDrawer?.agentId : undefined;
  const open = canView && !isEmpty(omit(agentDetailsDrawer, ['toggledAt']));
  const [agentAvailability, setAgentAvailability] = useState<IAgentWithAvailability['availability'] | null>(null);

  const canDelete = useHasAccess([PERMISSIONS.Users.DELETE, PERMISSIONS.BUSINESS_PARTNER]);
  const canDisable = useHasAccess([PERMISSIONS.Users.DISABLE, PERMISSIONS.BUSINESS_PARTNER]);
  const hasLeadManagement = useHasFeatureFlag(FEATURE_FLAGS.LEAD_MANAGEMENT);
  const hasScheduler = useHasFeatureFlag(FEATURE_FLAGS.SCHEDULER);
  const hasBookingLinkManagement = useHasAccess(
    [PERMISSIONS.BOOKING_PAGES.MANAGE, PERMISSIONS.BUSINESS_PARTNER]
  );

  const loading = useSelector((state: DefaultRootState) => state.loading.effects['user/fetchSingleAgent']);
  const agent = useSelector((state: DefaultRootState) => UsersSelectors.selectAgentDetails(state, agentId));

  const previousRoute = useRef<string | null>(null);

  useEffect(() => {
    if (!agentDetailsDrawer?.ignoreRouting) {
      if (open) {
        previousRoute.current = window.location.pathname;
        window.history.pushState({}, '', `/users/${agentId}`);
      } else if (previousRoute.current && typeof previousRoute.current === 'string') {
        if (previousRoute.current.includes('/users/')) {
          //   TODO
        }
        window.history.pushState({}, '', previousRoute.current);
      }

    }
  }, [open]);
  useEffect(() => {
    if (agentId) {
      dispatch({
        type: 'user/fetchSingleAgent',
        payload: {
          agentId,
        },
      });
      dispatch({
        type: 'user/fetchConversionalRoles',
        payload: {
          transferable: 1,
        },
      });
    }
  }, [agentId]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setActiveTab('general');
      setAgentAvailability(null);
      setDirty(false);
    }
  }, [open]);

  useEffect(() => {
    const tab = tabs.find(_tab => _tab.key === activeTab);
    if (tab?.drawerWidth)
      setDrawerWidth(tab?.drawerWidth);
  }, [activeTab]);

  const toggle = () => {
    if (open && dirty) {
      dispatch({ type: 'user/getAllUsers' });
      setDirty(false);
    }
    dispatch({
      type: 'user/toggleDetailsDrawer',
    });
  };

  function handleDisable() {
    Modal.confirm({
      title: intl.formatMessage({ id: 'pages.users.disableuserConfirmation', defaultMessage: 'Are you sure to disable this user?' }),
      okText: intl.formatMessage({ id: 'pages.users.disableUser', defaultMessage: 'Disable user' }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      onOk: async () => {
        const result: any = await disableUserRequest({ params: { userId: agentId } });
        if (result?.status === 200 || result?.data?.success !== false) {
          message.success(intl.formatMessage({ id: 'user.disable.success', defaultMessage: 'User disabled.' }));
          toggle();
          dispatch({ type: 'user/getAllUsers' });
        } else {
          message.error(intl.formatMessage({ id: 'common.error.generic', defaultMessage: 'An error occurred. Please try again.' }));
        }
      },
    });
  }

  function handleEnable() {
    Modal.confirm({
      title: intl.formatMessage({ id: 'pages.users.enableUserConfirmation', defaultMessage: 'Are you sure to enable this user?' }),
      okText: intl.formatMessage({ id: 'pages.users.enableUser', defaultMessage: 'Enable user' }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      onOk: async () => {
        const result: any = await enableUserRequest({ params: { userId: agentId } });
        if (result?.status === 200 || result?.data?.success !== false) {
          message.success(intl.formatMessage({ id: 'user.enable.success', defaultMessage: 'User enabled.' }));
          dispatch({ type: 'user/fetchSingleAgent', payload: { agentId } });
          dispatch({ type: 'user/getAllUsers' });
        } else {
          message.error(intl.formatMessage({ id: 'common.error.generic', defaultMessage: 'An error occurred. Please try again.' }));
        }
      },
    });
  }

  function handleDelete() {
    Modal.confirm({
      title: intl.formatMessage({ id: 'user.delete.confirmTitle', defaultMessage: 'Permanently delete user?' }),
      content: intl.formatMessage({ id: 'user.delete.confirmContent', defaultMessage: 'This will permanently delete this user. This cannot be undone.' }),
      okText: intl.formatMessage({ id: 'common.actions.delete_permanently', defaultMessage: 'Delete permanently' }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      okButtonProps: { danger: true },
      onOk: async () => {
        const result: any = await deleteUserRequest({ params: { userId: agentId } });
        if (result?.status === 204 || result?.data?.success !== false) {
          message.success(intl.formatMessage({ id: 'user.delete.success', defaultMessage: 'User deleted.' }));
          toggle();
          dispatch({ type: 'user/getAllUsers' });
        } else {
          message.error(intl.formatMessage({ id: 'common.error.generic', defaultMessage: 'An error occurred. Please try again.' }));
        }
      },
    });
  }
  const tabs = [
    {
      key: 'general',
      label: <F id="common.details" defaultMessage="Details" />,
      drawerWidth: 800,
      icon: <InfoCircleOutlined />,
      children: <AgentGeneralInfoTab agent={agent} onDirty={() => setDirty(true)} />,
    },
    {
      key: 'availability',
      label: (
        <F
          id="components.users.calendarSettings"
          defaultMessage="Calendar Settings"
        />
      ),
      icon: <ClockCircleOutlined />,
      children: (
        <AgentAvailabilityTab agent={agent} onGoToService={() => setActiveTab('services')} onDirty={() => setDirty(true)} />
      ),
    },
    {
      key: 'services',
      label: <F id={'pages.users.servicesAccess.title'} defaultMessage={'Services & Access'} />,
      drawerWidth: 1000,
      icon: <AppstoreOutlined />,
      children: <AgentServicesTab agent={agent} onDirty={() => setDirty(true)} />,
    },
    {
      key: 'tasks',
      label: <F id={'entity.task.plural'} defaultMessage={'Tasks'} />,
      drawerWidth: 1200,
      icon: <UserOutlined />,
      children: <AgentTasksTab agent={agent} />,
      hide: !hasLeadManagement,
    },
    {
      key: 'projects',
      label: <F id={'entity.lead.plural'} defaultMessage={'Projects'} />,
      drawerWidth: 800,
      icon: <ProjectOutlined />,
      children: <AgentProjectsTab agent={agent} />,
      hide: !hasLeadManagement,
    },
    {
      key: 'appointments',
      label: (
        <F id={'entity.appointment.plural'} defaultMessage={'Appointments'} />
      ),
      drawerWidth: 800,
      icon: <CalendarOutlined />,
      children: <AgentAppointmentsTab agent={agent} />,
      hide: !hasScheduler,
    },
    {
      key: 'booking_links',
      label: (
        <F id={'entity.booking_page.plural'} defaultMessage={'Booking Links'} />
      ),
      drawerWidth: 1200,
      icon: <LinkOutlined />,
      children: <AgentBookingLinksTab toggleDrawer={toggle} agent={agent} />,
      hide: !hasScheduler || !hasBookingLinkManagement,
    }
  ].filter((tab) => !tab.hide);

  const getTitle = () => (
    <Space>
      <F entity={INTL_ENTITY.AGENT} action={INTL_ACTION.DETAILS} />
    </Space>
  );

  return (
    <Drawer
      width={drawerWidth || 1200}
      title={getTitle()}
      open={open}
      onOpenChange={toggle}
      toggledAt={agentDetailsDrawer?.toggledAt}
      destroyOnHidden
      className={styles.agentDrawer}
    >
      <Spin spinning={loading}>
        <div className={styles.agentDrawerContainer}>
          {/* Agent Information Header */}
          <Card size="small" className={styles.agentHeader}>
            <Flex align={"center"} justify={"space-between"}>
              <Space size={16} align="start">
                <Avatar
                  size={agent.avatar ? 80 : 40}
                  src={agent.avatar}
                >
                  {agent.firstname?.[0]}{agent.lastname?.[0]}
                </Avatar>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {agent.firstname} {agent.lastname}
                  </Title>
                  <Text type="secondary">{agent.email}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Space size={8} wrap>
                      <AvailabilityGlance availability={agent?.availability?.working_hours} />
                      <AvailabilityTimezoneTag timezones={getAgentBranchTimezones(agent)} />
                    </Space>
                  </div>
                </div>
              </Space>
              {isAgentActive(agent) ? (
                canDisable && (
                  <Button icon={<StopOutlined />} onClick={handleDisable} size="small">
                    <F id="pages.users.disableUser" defaultMessage="Disable user" />
                  </Button>
                )
              ) : (
                <Space size={8}>
                  {canDisable && (
                    <Button icon={<SyncOutlined />} onClick={handleEnable} size="small">
                      <F id="pages.users.enableUser" defaultMessage="Enable user" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button danger icon={<DeleteOutlined />} onClick={handleDelete} size="small">
                      <F id="common.actions.delete_permanently" defaultMessage="Delete permanently" />
                    </Button>
                  )}
                </Space>
              )}
            </Flex>

          </Card>

          <div className={styles.agentDrawerTabs}>
            <div className={classNames(styles.agentDrawerMenu, { [styles.collapsed]: menuCollapsed })}>
              <Button
                shape="circle"
                color={'geekblue'}
                variant={'filled'}
                icon={menuCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setMenuCollapsed(!menuCollapsed)}
                className={styles.collapseToggle}
              />
              <Menu
                onClick={menuItem => setActiveTab(menuItem.key)}
                selectedKeys={[activeTab]}
                mode="inline"
                inlineCollapsed={menuCollapsed}
                items={tabs.map(tab => ({
                  key: tab.key,
                  label: tab.label,
                  icon: tab.icon,
                }))}
              />
            </div>
            <div className={styles.agentDrawerTabsContent}>
              {tabs.find(tab => tab.key === activeTab)?.children}
            </div>
          </div>
        </div>
      </Spin>
    </Drawer>
  );
};

export default React.memo(AgentDrawer);
